import { useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { previewAffiliateGroupChange } from "../../features/affiliates/affiliates"
import { groupChangeMessage } from "./affiliateGroupChange"

const IDLE = { pending: false, message: '' }

// Confirm-before-save for a change of an affiliate's contact groups: preview what the save would do
// to their files, show it, and run the save only on confirm. Same shape as useAffiliateStatusChange:
// one `busy` flag covers preview -> open -> save, and inFlightRef keeps ConfirmModal's own close after
// functionButton() from being read as a cancel. A refused preview still lets the operator save.
const useAffiliateGroupChangeConfirm = ({ userId, name, onConfirm }) => {
  const dispatch = useDispatch()
  const [state, setState] = useState(IDLE)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const inFlightRef = useRef(false)

  const release = () => {
    busyRef.current = false
    setBusy(false)
  }

  const save = async () => {
    inFlightRef.current = true
    try {
      await onConfirm()
    } finally {
      inFlightRef.current = false
      release()
    }
  }

  const requestSave = async (contactGroups) => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)

    const result = await dispatch(previewAffiliateGroupChange({ userId, contactGroups }))
    const preview = result.error ? null : result.payload?.data

    if (preview?.reason === 'unchanged') {
      await save()
      return
    }

    setState({ pending: true, message: groupChangeMessage({ name, preview, failed: !preview }) })
  }

  const confirm = async () => {
    if (!state.pending || inFlightRef.current) return
    setState(IDLE)
    await save()
  }

  const cancel = () => {
    if (inFlightRef.current) return
    setState(IDLE)
    release()
  }

  return {
    requestSave,
    confirm,
    cancel,
    pending: state.pending,
    message: state.message,
    busy,
  }
}

export default useAffiliateGroupChangeConfirm
